import { resolveExecutionModeV1 } from "@adpilot/shared";
import { useQuery } from "@tanstack/react-query";
import { fetchAiOperationsRunsPage } from "@/lib/ai-run-api";
import { canAccessApprovalQueue } from "@/lib/approval-access";
import { fetchApprovalProposals } from "@/lib/approval-api";
import { DEFAULT_APPROVAL_SEARCH } from "@/lib/approval-search";
import { fetchProductCenterProducts } from "@/lib/product-center-api";
import type { ReportingTimeZone } from "@/lib/reporting-time-zone";
import { fetchSystemAiPolicy } from "@/lib/settings-api";
import { useAuthStore } from "@/stores/auth";
import type { ReportingPeriod } from "@/stores/reporting-period";
import type { AiAutomationMode } from "./ai-status-card";

// "Last run", "Applied today", the 24h issue window and the per-campaign run bars
// all look at recent runs, independent of the selected reporting period.
const RECENT_RUNS_PERIOD: ReportingPeriod = { type: "preset", value: "7d" };

/** Recent AI runs across the analytics scope (same cache shape as the AI Operations page). */
export function useOverviewRecentRuns(scopeShopIds: string[], timeZone: ReportingTimeZone) {
  return useQuery({
    queryKey: [
      "ai-operations",
      "overview-runs",
      scopeShopIds.join(","),
      RECENT_RUNS_PERIOD,
      timeZone,
    ],
    queryFn: () => fetchAiOperationsRunsPage(RECENT_RUNS_PERIOD, timeZone, scopeShopIds),
    enabled: scopeShopIds.length > 0,
    staleTime: 30_000,
  });
}

/** Pending proposals across the scope, earliest review deadline first. */
export function useOverviewPendingProposals(scopeShopIds: string[], pageSize = 5) {
  const shops = useAuthStore((state) => state.shops);
  const canReview = scopeShopIds.some((shopId) => canAccessApprovalQueue(shops, shopId));
  const query = useQuery({
    queryKey: ["approvals", "overview-attention", scopeShopIds, pageSize],
    queryFn: () =>
      fetchApprovalProposals({
        ...DEFAULT_APPROVAL_SEARCH,
        reviewStatus: "pending",
        sortBy: "expiresAt",
        sortOrder: "asc",
        pageSize,
        shopIds: scopeShopIds.join(","),
      }),
    enabled: canReview && scopeShopIds.length > 0,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
  return { ...query, canReview };
}

/** Workspace AI mode and target ACoS from the system policy. */
export function useWorkspaceAiPolicySummary(aiMasterOn: boolean | undefined) {
  const query = useQuery({ queryKey: ["settings", "ai-policy"], queryFn: fetchSystemAiPolicy });
  const policy = query.data;
  const mode: AiAutomationMode | null = policy
    ? aiMasterOn === false || !policy.systemEnabled
      ? "off"
      : resolveExecutionModeV1(policy.policy)
    : null;
  return { mode, targetAcos: policy?.policy.targetAcos ?? null, query };
}

interface TopAsinsQueryInput {
  scopeShopIds: string[];
  startDate: string;
  endDate: string;
  asins?: string[];
  pageSize?: number;
}

/**
 * Top ASINs by ad sales. Product Center cannot sort by total (seller) sales and
 * returns one row per shop × ASIN, so this is the closest existing source.
 */
export function useOverviewTopAsins({
  scopeShopIds,
  startDate,
  endDate,
  asins = [],
  pageSize = 5,
}: TopAsinsQueryInput) {
  return useQuery({
    queryKey: [
      "product-center",
      "overview-top-asins",
      scopeShopIds,
      startDate,
      endDate,
      asins.join(","),
      pageSize,
    ],
    queryFn: ({ signal }) =>
      fetchProductCenterProducts(
        {
          shopIds: scopeShopIds,
          startDate,
          endDate,
          dimension: "ASIN",
          page: 1,
          pageSize,
          active: "all",
          sortBy: "adSales",
          sortDirection: "desc",
          asins,
        },
        signal,
      ),
    enabled: scopeShopIds.length > 0,
  });
}
